// How big is this product? ONE answer, derived, for every public page that states it.
//
// Four different sizes were being told to a visitor, in two different nouns, none of them
// computed: /modules said "115 documented capabilities", /pricing said "the 23 modules every
// school gets", and the live CORE_MODULES registry had 28 in it. A buyer who reads 115 on one
// page and 23 on the next does not conclude that the two pages count different things — they
// conclude the pricing page is showing them a fifth of the product.
//
// The two registries genuinely ARE different units and a school needs both, so the fix is to
// state the RELATIONSHIP rather than to pick a favourite:
//
//   115 capabilities  — what a school DOES with it   (the /modules catalogue, 9 workspaces)
//    58 modules       — what a PLAN switches on      (28 core + 30 gateable entitlements)
//
// Counts are SERVED (`pricing_integrity.catalogue`, computed in core/marketing.catalogue_counts
// from the live registries) and fall back to deriving themselves from `plan_matrix`, which the
// same payload already carries. Nothing here may hard-code a number: that is the defect.
export function catalogueCounts(cms) {
  const served = cms?.pricing_integrity?.catalogue || null;
  const matrix = cms?.plan_matrix || null;
  const int = (value) => (Number.isInteger(value) && value > 0 ? value : null);
  const len = (value) => (Array.isArray(value) && value.length ? value.length : null);

  // plan_matrix carries the core-module ids and one row per gateable feature, so the page can
  // still count itself honestly if an older cached payload predates the served block.
  const core = int(served?.core_modules) ?? len(matrix?.core_modules);
  const gateable = int(served?.gateable_modules) ?? len(matrix?.features);
  const total = int(served?.total_modules) ?? (core && gateable ? core + gateable : null);

  const byPlan = served?.modules_by_plan && typeof served.modules_by_plan === "object"
    ? served.modules_by_plan
    : derivedByPlan(matrix, core);

  return {
    core,
    gateable,
    total,
    byPlan: byPlan || {},
    // Capability/workspace counts live in the frontend catalogue and are MIRRORED by the server
    // (marketing.CAPABILITY_ROUTE_COUNT). Served-only on purpose: importing moduleCatalog here
    // would drag the whole 115-entry catalogue into the pricing chunk for two integers.
    capabilities: int(served?.capabilities),
    workspaces: int(served?.workspaces),
  };
}

// A plan's module count = the core set every plan gets + the gateable features it switches on.
// Derived from the same `plans[].features` booleans the comparison table renders, so a card's
// count and the table beneath it can never disagree.
function derivedByPlan(matrix, core) {
  if (!Array.isArray(matrix?.plans) || !core) return null;
  const out = {};
  for (const plan of matrix.plans) {
    const features = plan?.features;
    if (!plan?.id || !features || typeof features !== "object") continue;
    out[plan.id] = core + Object.values(features).filter(Boolean).length;
  }
  return Object.keys(out).length ? out : null;
}

// Copy carries TOKENS, never numbers. "{tax}" was already the live tax-display string; the count
// tokens join it on the same rule, so a platform editor can position a figure in a sentence but
// can never state one of their own — and a figure can never go stale behind copy nobody re-read.
//
// A token whose value is unavailable resolves to NOTHING rather than leaking "{core_modules}" to
// a visitor; callers that need a whole sentence to disappear check the count itself first.
export function fillCopyTokens(text, { tax = "", counts = null } = {}) {
  const map = {
    "{tax}": tax,
    "{core_modules}": counts?.core ? String(counts.core) : "",
    "{total_modules}": counts?.total ? String(counts.total) : "",
    "{capabilities}": counts?.capabilities ? String(counts.capabilities) : "",
    "{workspaces}": counts?.workspaces ? String(counts.workspaces) : "",
  };
  let out = String(text || "");
  for (const [token, value] of Object.entries(map)) {
    if (out.includes(token)) out = out.split(token).join(value);
  }
  // An unavailable count leaves a double space where its token stood; collapse it so the
  // degraded sentence still reads as a sentence.
  return out.replace(/ {2,}/g, " ").trim();
}
